import type {
  Order,
  OrderItemStatus,
  OrderStatus,
} from "@/redux/services/order/order.type";

/** Kitchen board columns: "Nouvelles", "En cours", "Prêt". */
export type KdsColumn = "waiting" | "preparing" | "ready";

export const KDS_COLUMN_ORDER: KdsColumn[] = ["waiting", "preparing", "ready"];

export type KdsItemType = "item" | "combo" | "formula";

export type KdsMoveStep =
  | { kind: "order"; status: OrderStatus }
  | {
      kind: "item";
      itemType: KdsItemType;
      itemId: string;
      status: OrderItemStatus;
    };

/**
 * The column an order shows in. A ready/served order with a fresh add-on
 * still has kitchen work, so it stays in "En cours" until every line is
 * ready.
 */
export const kdsColumnFor = (
  order: Pick<Order, "status" | "hasUnpreparedItems">
): KdsColumn | undefined => {
  if (
    (order.status === "ready" || order.status === "served") &&
    order.hasUnpreparedItems
  ) {
    return "preparing";
  }
  switch (order.status) {
    case "accepted":
      return "waiting";
    case "preparing":
      return "preparing";
    case "ready":
      return "ready";
    default:
      return undefined;
  }
};

const ITEM_STEPS: Record<OrderItemStatus, OrderItemStatus[]> = {
  pending: ["preparing", "ready"],
  preparing: ["ready"],
  ready: [],
};

// Order transitions needed to reach "ready" (workflow order_status:
// accepted → preparing → ready, no shortcut).
const ORDER_STEPS_TO_READY: Partial<Record<OrderStatus, OrderStatus[]>> = {
  accepted: ["preparing", "ready"],
  preparing: ["ready"],
};

/**
 * The API calls a drag on the kitchen board needs, in order. Only forward
 * moves do anything.
 *
 * - To "En cours": start preparing the order (accepted → preparing).
 * - To "Prêt": advance every line that is not ready, the way the line
 *   buttons do (pending → preparing → ready). The API then moves the order
 *   itself: the first line in preparation starts the order, the last ready
 *   line marks it ready. Changing only the order status would be refused
 *   from accepted (no accepted → ready transition) and would leave the lines
 *   unprepared from preparing. Without lines to advance, the order status
 *   is walked to ready instead.
 */
export const planKdsMove = (order: Order, target: KdsColumn): KdsMoveStep[] => {
  const current = kdsColumnFor(order);
  if (
    !current ||
    KDS_COLUMN_ORDER.indexOf(target) <= KDS_COLUMN_ORDER.indexOf(current)
  ) {
    return [];
  }

  if (target === "preparing") {
    return [{ kind: "order", status: "preparing" }];
  }

  const lines: {
    itemType: KdsItemType;
    id: string;
    status: OrderItemStatus;
  }[] = [
    ...(order.orderItems ?? []).map((i) => ({
      itemType: "item" as const,
      id: i.id,
      status: (i.itemStatus ?? "pending") as OrderItemStatus,
    })),
    ...(order.menuComboOrderItems ?? []).map((i) => ({
      itemType: "combo" as const,
      id: i.id,
      status: (i.itemStatus ?? "pending") as OrderItemStatus,
    })),
    ...(order.menuFormulaOrderItems ?? []).map((i) => ({
      itemType: "formula" as const,
      id: i.id,
      status: (i.itemStatus ?? "pending") as OrderItemStatus,
    })),
  ];

  const itemSteps: KdsMoveStep[] = lines.flatMap((line) =>
    ITEM_STEPS[line.status].map((status) => ({
      kind: "item" as const,
      itemType: line.itemType,
      itemId: line.id,
      status,
    }))
  );
  if (itemSteps.length > 0) return itemSteps;

  return (ORDER_STEPS_TO_READY[order.status] ?? []).map((status) => ({
    kind: "order" as const,
    status,
  }));
};
